import { SummaryCardSession } from './summary-card';
import { offline, requireConnection } from './connectivity';
// The browser observes durable executions. Its lifetime never controls a worker.
export class RunSession {
    public onUnauthorized: (token: string) => void = () => {};
    public card = new SummaryCardSession((path, method, body) => this.request(path, method, body), () => this.id);
    public run: any = null;
    public list: any[] = [];
    public deletions: any[] = [];
    public filters: Record<string, string> = { profile: '', feature: '', status: '', from: '', to: '', offset: '0' };
    listQuery() { return '?' + new URLSearchParams(Object.entries(this.filters).filter(([_,v]) => !!v)).toString(); }
    public connection = '';
    public connected = false;
    public message = '';
    public busy = false;
    public startMode = 'automatic';
    public revisionReason = '';
    public personalContext = '';
    public changeRevision: any = null;
    public printUrl = '';
    public printFilename = '';
    public printPdfUrl = '';
    public printPdfFilename = '';
    public printMessage = '';
    public printBusy = false;
    public exportVersion = '';
    private epoch = 0;
    private pollSerial = 0;
    private printSerial = 0;
    private timer?: ReturnType<typeof setTimeout>;
    private token = '';
    private id = '';
    private changed: () => void = () => {};
    private pendingStart: any = null;
    private pendingCommand: any = null;
    private pendingRevision: any = null;
    connectivity() {
        if (offline()) { clearTimeout(this.timer); this.connected = false; this.connection = '인터넷 연결이 끊겼어요. 마지막으로 확인한 내용을 보여 드려요.'; }
    }
    private blockOffline() {
        if (!offline()) return false;
        this.message = '현재 오프라인입니다. 요청을 보내지 않았습니다. 연결 후 직접 다시 눌러 주세요.';
        return true;
    }

    stop(clear = false) {
        this.epoch++; clearTimeout(this.timer); this.busy = false; this.connected = false; this.card.clear();
        this.clearPrint();
        if (clear) { this.run = null; this.list = []; this.deletions = []; this.exportKeys = {}; this.pendingStart = null; this.pendingCommand = null; this.pendingRevision = null; this.message = ''; this.revisionReason = ''; this.personalContext = ''; this.changeRevision = null; }
    }
    async request(path: string, method = 'GET', body?: any, token = this.token) {
        requireConnection();
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), (path.endsWith('/print-prepare') || path.endsWith('/report-export') || path.endsWith('/summary-card')) ? 100000 : 15000);
        try {
            const response = await fetch('/api/saju/runs' + path, { method, cache: 'no-store', credentials: 'include', signal: controller.signal,
                headers: { Authorization: 'Bearer ' + token, ...(body ? { 'Content-Type': 'application/json' } : {}) },
                body: body ? JSON.stringify(body) : undefined });
            const data = await response.json();
            if ([401,403].includes(response.status) && token === this.token) this.onUnauthorized(token);
            if (!response.ok) throw { status: response.status, code: data.error_code };
            return data.data;
        } finally { clearTimeout(timeout); }
    }
    observe(id: string, token: string, changed: () => void) {
        const same = this.id === id && this.token === token;
        this.stop(!same); this.id = id; this.token = token; this.changed = changed;
        if (!token) { this.connection = '인증 연결 후 저장된 실행을 복구합니다.'; changed(); return; }
        this.connection = '저장된 상태를 확인하고 있습니다.';
        void this.poll(this.epoch);
    }
    accept(value: any) {
        if (value.id !== this.id || !Number.isSafeInteger(value.version)) return;
        if (this.run && this.run.execution_kind !== 'preparation' && value.execution_kind === 'preparation') return;
        if (this.run?.status === 'cancelled' && value.status !== 'cancelled') return;
        if (this.run && value.execution_kind === this.run.execution_kind && value.version < this.run.version) return;
        // A read cannot change the chapter projection at the same server revision.
        // Retain its objects so long-document bindings/disclosures stay undisturbed.
        if (this.run && value.version === this.run.version && value.reader_contract === this.run.reader_contract)
            value.chapters = this.run.chapters;
        if (this.run && JSON.stringify(this.run.comparison?.print_source) !== JSON.stringify(value.comparison?.print_source)) this.clearPrint();
        this.run = value;
    }
    clearPrint() {
        this.downloadSource = null;
        this.exportVersion = '';
        this.printSerial++;
        if (this.printUrl) URL.revokeObjectURL(this.printUrl);
        if (this.printPdfUrl) URL.revokeObjectURL(this.printPdfUrl);
        this.printPdfUrl = ''; this.printPdfFilename = '';
        this.printUrl = ''; this.printFilename = ''; this.printMessage = ''; this.printBusy = false;
    }
    async preparePrint(restore = false) {
        if (this.printBusy || !this.run?.comparison?.content_complete) return;
        const epoch = this.epoch, id = this.id, source = JSON.stringify(this.run.comparison.print_source);
        this.clearPrint(); this.printBusy = true;
        const serial = this.printSerial;
        try {
            const artifact = await this.request('/' + id + '/print-prepare', restore ? 'GET' : 'POST');
            if (epoch !== this.epoch || serial !== this.printSerial || id !== this.id || !this.run || source !== JSON.stringify(this.run.comparison?.print_source)) return;
            if (!artifact.print_ready) {
                this.printMessage = artifact.state === 'not_created'
                    ? '아직 저장된 인쇄 파일이 없어요. 풀이 결과는 보관되어 있습니다. 인쇄·저장 준비를 눌러 주세요.'
                    : '인쇄 파일을 준비하지 못했어요. 풀이 결과는 보관되어 있습니다. 잠시 뒤 인쇄·저장 준비를 다시 눌러 주세요.';
                return;
            }
            const bytes = Uint8Array.from(atob(artifact.pdf_base64), c => c.charCodeAt(0));
            this.printPdfUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
            this.printPdfFilename = artifact.pdf_filename;
            this.printUrl = URL.createObjectURL(new Blob([artifact.html], { type: 'text/html;charset=utf-8' }));
            this.printFilename = artifact.filename;
            this.downloadSource = {path:'/' + id + '/print-prepare', html:artifact.html, pdf:artifact.pdf_base64};
            this.printMessage = '인쇄 파일이 준비됐어요. A4 세로 한 장 PDF를 내려받을 수 있습니다.';
        } catch (error: any) {
            if (epoch !== this.epoch || serial !== this.printSerial) return;
            if ([401,403,404].includes(error.status)) this.failed(error);
            this.printMessage = ['F3_PRINT_INCOMPLETE', 'F3_PRINT_INVALID_SOURCE'].includes(error.code)
                ? '아직 인쇄 파일을 준비할 수 없어요. 저장된 풀이를 먼저 읽고, 남은 풀이가 완성되면 다시 눌러 주세요.' : '인쇄 자료를 불러오지 못했어요. 풀이 결과는 보관되어 있습니다. 잠시 뒤 저장된 출력물 불러오기를 다시 눌러 주세요.';
        } finally { if (epoch === this.epoch && serial === this.printSerial) this.printBusy = false; }
    }
    async exportReport(mode: 'new' | 'retry' | 'restore' = 'new') {
        if (this.printBusy || !['F1', 'F2'].includes(this.run?.input?.feature_id) || this.run?.question) return;
        const epoch = this.epoch, id = this.id, token = this.token;
        this.clearPrint(); this.printBusy = true;
        const serial = this.printSerial;
        try {
            const owner = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token)))).map(x => x.toString(16).padStart(2, '0')).join('');
            if (epoch !== this.epoch || serial !== this.printSerial) return;
            const storageKey = 'saju-export:' + owner + ':' + id;
            let key = this.exportKeys[storageKey] || '';
            try { key = sessionStorage.getItem(storageKey) || key; } catch (_) {}
            if (mode !== 'new' && !/^[a-f0-9]{32}$/.test(key)) { this.printMessage = '이 탭에 고정된 출력 요청이 없습니다. 현재 결과로 새 파일을 준비해 주세요.'; return; }
            if (mode === 'new') key = crypto.randomUUID().replace(/-/g, '');
            this.exportKeys[storageKey] = key;
            try { sessionStorage.setItem(storageKey, key); } catch (_) {}
            const artifact = await this.request('/' + id + '/report-export' + (mode === 'restore' ? '?request_key=' + key : ''), mode === 'restore' ? 'GET' : 'POST', mode === 'restore' ? undefined : { request_key: key }, token);
            if (epoch !== this.epoch || serial !== this.printSerial || id !== this.id || token !== this.token || !this.run) return;
            this.printUrl = URL.createObjectURL(new Blob([artifact.html], { type: 'text/html;charset=utf-8' }));
            this.printFilename = artifact.filename;
            this.downloadSource = {path:'/' + id + '/report-export?request_key=' + key, html:artifact.html, pdf:artifact.pdf_base64};
            if (artifact.pdf_ready) {
                const bytes = Uint8Array.from(atob(artifact.pdf_base64), c => c.charCodeAt(0));
                this.printPdfUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' })); this.printPdfFilename = artifact.pdf_filename;
            }
            this.printMessage = (artifact.partial ? '부분 보고서 · 미완성 항목 표시. ' : '') + artifact.message;
            this.exportVersion = '고정 시각 ' + artifact.created_at + ' · 결과 버전 ' + artifact.run_version + ' · 명식 ' + artifact.revision_id + ' · 프롬프트 ' + artifact.prompt_version;
        } catch (error: any) {
            if (epoch !== this.epoch || serial !== this.printSerial) return;
            this.printMessage = error.status === 410 ? '보관 기간이 지났습니다. 현재 결과로 새 파일을 준비해 주세요.' : '파일 준비 응답을 확인하지 못했습니다. 저장된 파일 불러오기 또는 같은 버전 다시 만들기를 이용해 주세요. 보고서는 보존됩니다.';
            if ([401,403,404].includes(error.status)) this.failed(error);
        } finally { if (epoch === this.epoch && serial === this.printSerial) this.printBusy = false; }
    }
    private exportKeys: Record<string, string> = {};
    private downloadSource: any = null;
    async downloadPrint(event: Event, kind: 'html' | 'pdf', open = false) {
        event.preventDefault();
        if (this.printBusy || !this.downloadSource || !this.run) return;
        const epoch = this.epoch, serial = this.printSerial, expected = this.downloadSource;
        this.printBusy = true;
        try {
            const current = await this.request(expected.path);
            if (epoch !== this.epoch || serial !== this.printSerial || !this.run) return;
            if (current.html !== expected.html || (kind === 'pdf' && current.pdf_base64 !== expected.pdf)) throw new Error('changed');
            const link = document.createElement('a');
            link.href = kind === 'pdf' ? this.printPdfUrl : this.printUrl;
            if (open) { link.target = '_blank'; link.rel = 'noopener noreferrer'; }
            else link.download = kind === 'pdf' ? this.printPdfFilename : this.printFilename;
            link.click();
        } catch (error: any) {
            if (epoch !== this.epoch || serial !== this.printSerial) return;
            this.clearPrint();
            if ([401,403,404].includes(error.status)) this.failed(error);
            this.printMessage = '권한·삭제·보관 기간을 확인하지 못해 파일 열기와 저장을 차단했습니다.';
        } finally { if (epoch === this.epoch && serial === this.printSerial) this.printBusy = false; }
    }
    private failed(error: any) {
        this.connected = false;
        if ([401,403,404].includes(error.status)) {
            this.card.clear();
            this.clearPrint();
            this.run = null; this.list = [];
            this.connection = error.status === 404 ? '실행을 찾을 수 없거나 열람 권한이 없습니다.' : '인증 연결을 확인해 주세요.';
        } else this.connection = '연결을 확인하지 못했어요. 마지막으로 확인한 내용을 보여 드려요. 새 풀이를 요청하지 않고 기존 요청을 다시 확인합니다.';
    }
    async poll(epoch = this.epoch) {
        clearTimeout(this.timer);
        if (!this.token || offline()) { this.connectivity(); return; }
        // Error responses have no server version. Fence overlapping polls as well
        // so an older success cannot restore a result after a newer 404/401.
        const serial = ++this.pollSerial;
        try {
            const [value, deletions] = await Promise.all([this.request(this.id ? '/' + this.id : this.listQuery()), this.id ? Promise.resolve([]) : this.request('/deletions')]);
            if (epoch !== this.epoch || serial !== this.pollSerial) return;
            if (this.id) this.accept(value); else { this.list = value; this.deletions = deletions; }
            this.connected = true; this.connection = '';
        } catch (error: any) { if (epoch === this.epoch && serial === this.pollSerial) this.failed(error); }
        finally {
            if (epoch === this.epoch && serial === this.pollSerial) {
                this.changed();
                clearTimeout(this.timer);
                const delay = !this.connected ? 5000 : runPresentation(this.run).poll;
                if (!offline() && (!this.id || delay)) this.timer = setTimeout(() => this.poll(epoch),
                    typeof document !== 'undefined' && document.hidden ? 30000 : delay || 15000);
            }
        }
    }
    async recoverLocal() {
        if (this.blockOffline() || this.busy || !this.run?.local_recovery_available) return;
        const epoch = this.epoch;
        this.busy = true;
        try {
            const value = await this.request('/' + this.id + '/recover', 'POST', {});
            if (epoch === this.epoch) { this.accept(value); this.message = ''; }
        } catch (error: any) {
            if (epoch === this.epoch) this.message = '저장을 아직 마무리하지 못했어요. 보관한 응답으로 다시 시도해 주세요.';
        } finally { if (epoch === this.epoch) { this.busy = false; this.changed(); } }
    }
    async start(featureId: string, revisionId: string, token: string, photo?: { draft_id: string; image_ids: string[]; draft_version?: number }) {
        if (this.blockOffline()) return null;
        if (this.busy) return null;
        // The server resolves the owned profile and current public release. Never
        // send a displayed chart, another profile, or an earlier run's answers.
        const body = photo ? { feature_id: featureId, ...photo } : { feature_id: featureId, revision_id: revisionId };
        const signature = JSON.stringify(body);
        if (this.pendingStart?.signature !== signature || this.token !== token)
            this.pendingStart = { signature, body };
        this.token = token; const epoch = this.epoch;
        this.busy = true; this.message = '';
        try {
            const data = await this.request('', 'POST', this.pendingStart.body, token);
            if (epoch !== this.epoch) return null;
            this.pendingStart = null;
            return data.run.id;
        } catch (error: any) {
            if (epoch !== this.epoch) return null;
            this.message = error.code === 'GENERATION_LIMIT_REACHED' ? '연결 시험 호출 한도에 도달했습니다. 저장한 결과를 확인해 주세요.' : error.code === 'GENERATION_CONFIGURATION_REQUIRED' ? '풀이를 시작하려면 ChatGPT 연결이 필요해요. 연결 기능은 준비 중이며 사진과 선택한 사주는 보관되어 있어요.'
                : error.status === 409 || error.status === 422 ? '저장한 사주 버전과 선택한 풀이를 다시 확인해 주세요.'
                : '접수 여부를 확인하지 못했습니다. 같은 요청 다시 보내기 또는 보관함에서 실행을 확인해 주세요.';
            return null;
        } finally { if (epoch === this.epoch) this.busy = false; }
    }
    async preparationRetry() {
        if (this.blockOffline() || this.busy || runPresentation(this.run).action !== 'prepare-retry') return;
        const epoch = this.epoch, observedSerial = this.pollSerial;
        this.busy = true; this.message = '';
        try {
            const value = await this.request('/' + this.id + '/prepare-retry', 'POST', { expected_version: this.run.preparation.version });
            if (epoch === this.epoch && observedSerial === this.pollSerial) this.accept(value);
        } catch { if (epoch === this.epoch) this.message = '같은 실행의 재시도를 확인하지 못했습니다. 상태를 다시 조회해 주세요.'; }
        finally { if (epoch === this.epoch) { this.busy = false; await this.poll(epoch); } }
    }
    async answerPhoto(action: any) {
        if (this.blockOffline() || this.busy || !this.run?.preparation?.clarification) return;
        const epoch = this.epoch;
        this.busy = true; this.message = '';
        try {
            const value = await this.request('/' + this.id + '/clarify', 'POST', { token: this.run.preparation.clarification.token, action });
            if (epoch === this.epoch) this.accept(value);
        } catch (error: any) {
            if (epoch === this.epoch) this.message = error.status === 409 ? '사진이나 답변이 변경되었어요. 최신 질문을 확인해 주세요.' :
                error.status === 422 ? '수정할 값을 확인해 주세요.' : '답변 저장 여부를 확인하지 못했어요. 상태를 다시 조회해 주세요. 사진은 보관되어 있어요.';
        } finally { if (epoch === this.epoch) { this.busy = false; await this.poll(epoch); } }
    }
    async startPrepared(prepared: any, token: string) {
        if (this.blockOffline() || this.busy) return null;
        const fixed = {
            feature_id: prepared.feature_id, release_hash: prepared.release_hash,
            profile_id: prepared.profile_id, revision_id: prepared.revision_id,
            answers: prepared.run_input.answers, progress_mode: this.startMode
        };
        const signature = JSON.stringify(fixed);
        if (this.pendingStart?.signature !== signature || this.token !== token)
            this.pendingStart = { signature, body: { request_key: crypto.randomUUID(), ...fixed } };
        this.token = token; const epoch = this.epoch;
        this.busy = true; this.message = '';
        try {
            const data = await this.request('', 'POST', this.pendingStart.body, token);
            if (epoch !== this.epoch) return null;
            this.pendingStart = null;
            return data.run.id;
        } catch (error: any) {
            if (epoch !== this.epoch) return null;
            this.message = error.code === 'GENERATION_CONFIGURATION_REQUIRED'
                ? '풀이를 시작하려면 ChatGPT 연결이 필요해요. 연결 기능은 준비 중이며 사진과 선택한 사주는 보관되어 있어요.'
                : '접수 여부를 확인하지 못했습니다. 같은 요청 다시 보내기 또는 보관함에서 실행을 확인해 주세요.';
            return null;
        } finally { if (epoch === this.epoch) this.busy = false; }
    }
    async command(action: string, task?: any, acknowledged = false, extra: any = {}) {
        if (this.blockOffline()) return;
        if (this.busy || !this.run) return;
        const epoch = this.epoch, id = this.id, observedSerial = this.pollSerial;
        const intent = [id,action,task?.id,task?.current_attempt,acknowledged,JSON.stringify(extra)].join(':');
        // Keep the same request after a lost response, including after a page reload.
        let saved: any;
        try { saved = JSON.parse(sessionStorage.getItem('saju-run-command') || 'null'); } catch { }
        if (!this.pendingCommand && saved?.intent === intent) this.pendingCommand = saved;
        if (this.pendingCommand?.intent !== intent) this.pendingCommand = { intent, action,
            body: { request_key: crypto.randomUUID(), expected_version: this.run.version,
                ...extra,
                ...(task ? { task_id: task.id, expected_attempt: task.current_attempt, acknowledge_duplicate_charge: acknowledged } : {}) } };
        const pending = this.pendingCommand;
        try { sessionStorage.setItem('saju-run-command', JSON.stringify(pending)); } catch { }
        this.busy = true; this.message = '';
        try {
            const value = await this.request('/' + id + '/' + action, 'POST', pending.body);
            if (epoch !== this.epoch) return;
            if (observedSerial === this.pollSerial) this.accept(value);
            this.pendingCommand = null;
            try { sessionStorage.removeItem('saju-run-command'); } catch { }
            this.message = '';
        } catch (error: any) {
            if (epoch !== this.epoch) return;
            if (error.status && error.status < 500) {
                this.pendingCommand = null;
                try { sessionStorage.removeItem('saju-run-command'); } catch { }
                this.message = '상태 또는 권한이 변경되었습니다. 최신 상태를 확인한 뒤 가능한 동작을 선택해 주세요.';
            } else this.message = '요청 응답을 확인하지 못했습니다. 같은 버튼으로 다시 확인해 주세요.';
        } finally { if (epoch === this.epoch) { this.busy = false; await this.poll(epoch); } }
    }
    async progress(mode?: string) {
        if (!this.run?.progression) return;
        const number = this.run.progression.current_chapter;
        return this.command('progress', undefined, false, mode ? { operation: 'mode', mode } :
            { operation: 'next', chapter: number, chapter_version: this.run.chapters[number - 1].version });
    }
    async prepareRawChange(request: (path: string) => Promise<any>) {
        if (!this.run || this.busy) return;
        const epoch = this.epoch, id = this.id;
        this.busy = true; this.message = '';
        try {
            const value = await request('/' + this.run.input.profile_id);
            if (epoch !== this.epoch || id !== this.id) return;
            this.changeRevision = value.current_revision;
            this.message = this.changeRevision?.id === this.run.input.revision_id ? '현재 실행과 같은 명식 버전입니다. 명식 확인 화면에서 수정·저장해 주세요.' : '아래 새 명식 버전과 재생성 범위를 확인해 주세요.';
        } catch { if (epoch === this.epoch) this.message = '저장된 명식을 확인하지 못했습니다.'; }
        finally { if (epoch === this.epoch) this.busy = false; }
    }
    async amend(kind: string, chapter = 2) {
        if (this.blockOffline()) return null;
        if (this.busy || !this.run || !this.revisionReason.trim()) return null;
        const epoch = this.epoch, id = this.id;
        const comparison = ['A', 'B', 'synthesis'].includes(kind);
        const action = kind === 'interpretation' || comparison ? 'revise' : 'change';
        const payload = { reason: this.revisionReason, ...(comparison ? { perspective: kind } : kind === 'interpretation' ? { chapter } :
            kind === 'raw_data' ? { kind, revision_id: this.changeRevision?.id } :
            { kind, answers: { ...this.run.input.run_input.answers, concern: [this.run.input.run_input.answers.concern, this.personalContext].filter(Boolean).join('\n\n') } }) };
        const signature = JSON.stringify([id, action, payload]);
        if (this.pendingRevision?.signature !== signature) this.pendingRevision = { signature,
            body: { ...payload, request_key: crypto.randomUUID(), expected_version: this.run.version } };
        this.busy = true; this.message = '';
        try {
            const value = await this.request('/' + id + '/' + action, 'POST', this.pendingRevision.body);
            if (epoch !== this.epoch) return null;
            this.pendingRevision = null;
            return value.run.id;
        } catch (error: any) {
            if (epoch !== this.epoch) return null;
            if (error.status && error.status < 500) this.pendingRevision = null;
            this.message = '개정 접수를 확인하지 못했습니다. 최신 상태·입력 길이와 기준을 확인해 주세요. 응답 유실 시 같은 버튼으로 다시 확인할 수 있습니다.';
            return null;
        } finally { if (epoch === this.epoch) { this.busy = false; await this.poll(epoch); } }
    }
}

export const PROGRESS_STATES: Record<string,string> = { question_wait:'추가 질문 대기 · 답변 확인 후 계속하기를 선택해 주세요.', generating:'현재 장 처리 중', chapter_confirmation:'완성된 장 확인 후 다음 장을 시작할 수 있습니다.', needs_input:'핵심 자료 확인이 필요합니다. 필요한 항목만 아래에서 확인해 주세요.', retry_required:'현재 장의 실패·복구 항목을 확인해 주세요.', paused:'일시정지 · 같은 실행 재개 가능', cancelled:'최종 취소 · 새 실행으로 다시 시작', superseded:'기준 변경 · 연결된 개정 실행에서 계속 진행', completed:'모든 장 생성 완료 · 내용 검증은 별도', review_failed:'내용 검증 미통과 · 개정 필요' };

export const RUN_STATES: Record<string,string> = { queued:'대기', running:'생성 중', partial:'부분 결과 저장됨', completed:'풀이 완료', failed:'실패 항목 확인 필요', waiting_input:'입력 확인 대기', cancelled:'최종 취소' };
// A display projection of the existing journal/tasks, never a second saved lifecycle.
export function runPresentation(run: any): { title: string; detail: string; action: string; label: string; cancel: boolean; poll: number; task?: any } {
    const show = (title: string, detail: string, action = '', label = '', cancel = false, poll = 0, task?: any) =>
        ({ title, detail, action, label, cancel, poll, task });
    if (!run) return show('기존 요청을 확인하고 있어요', '보관한 요청과 풀이를 불러옵니다.', 'poll', '기존 요청 확인', false, 5000);
    const kept = run.preparation ? '사진은 보관되어 있어요.' : '저장한 사주와 풀이 내용은 보관되어 있어요.';
    if (run.status === 'cancelled') return show('풀이를 그만했어요', kept + ' 그만하기는 사진이나 저장한 내용을 삭제하지 않아요.');
    const prep = run.execution_kind === 'preparation' ? run.preparation : null;
    const tasks = (run.tasks || []).filter((t: any) => !t.connection_deferred);
    const failures = prep?.state === 'failed' ? [prep] : tasks.filter((t: any) =>
        (t.error_code || t.recovery_required) && !(t.status === 'waiting_input' && t.failure_kind === 'input'));
    if (run.local_recovery_available) {
        const completed = tasks.some((t: any) => t.delivery_state === 'completed_pending_save');
        return show(completed ? '받은 풀이의 저장을 마무리해 주세요' : '중단된 작업 기록을 확인해 주세요',
            kept + (completed ? ' 다시 시도하면 받은 응답만 저장해요. 새 풀이를 요청하지 않아요.' : ' 다시 시도하면 보관된 기록을 먼저 확인해요. 새 요청은 보내지 않아요.'), 'recover', '다시 시도');
    }
    if (tasks.some((t: any) => t.status === 'running' && t.delivery_state === 'completed_pending_save'))
        return show('받은 풀이를 저장하고 있어요', kept + ' 저장이 끝나면 아래에서 읽을 수 있어요.', 'poll', '저장 상태 확인', false, 2500);
    const unknown = failures.find((t: any) => t.failure_kind === 'unknown' || t.recovery_required);
    if (unknown) return show('기존 요청의 처리 여부를 확인해야 해요', kept + ' 처리 완료 여부가 아직 확인되지 않아 다시 보내지 않아요. 저장 기록은 자동으로 확인하지만, 서비스 측 처리 확인이 필요해요.', 'poll', '기존 요청 확인', false, 30000);
    const restricted = failures.find((t: any) => connectionFailure(t.error_code));
    if (restricted) {
        const message = connectionFailure(restricted.error_code)!;
        return show(message.title, kept + ' ' + message.detail);
    }
    const configuration = failures.find((t: any) => t.failure_kind === 'configuration' && !(prep ? t.retryable : t.can_retry));
    if (configuration) return show('풀이를 시작하려면 ChatGPT 연결이 필요해요', kept + ' 연결 기능은 준비 중이에요. 연결이 준비되면 이 요청에서 이어갈 수 있어요.', 'poll', '복구 상태 확인', false, 15000);
    const retry = failures.find((t: any) => prep ? t.retryable : t.can_retry);
    if (retry) return show(retry.failure_kind === 'configuration' ? '이어서 시도할 수 있어요' : '일시적인 오류로 멈췄어요', kept + ' 다시 시도하면 멈춘 단계부터 이어가요. 완성된 내용은 다시 만들지 않아요.', prep ? 'prepare-retry' : 'retry', '다시 시도', false, 15000, prep ? undefined : retry);
    if (failures.length) return show('이 단계를 이어가지 못했어요', kept + (failures[0].failure_kind === 'input' ? ' 시작 이후 입력이 바뀌었어요. 보관한 사진과 입력을 확인해 주세요.' : ' 서비스에서 원인을 확인해야 해요. 계속 기다리거나 사진을 다시 올릴 필요는 없어요.'), failures[0].failure_kind === 'input' ? 'input' : 'poll', failures[0].failure_kind === 'input' ? '입력 확인' : '복구 상태 확인', false, 15000);
    if (prep) {
        if (prep.state === 'needs_clarification') return show('추가 정보가 필요해요', '아래에서 확인이 필요한 부분만 알려 주세요. 답변 뒤 같은 요청을 이어갑니다.', 'clarify', '필요한 정보 확인', true, 15000);
        if (prep.state === 'waiting_upload') return show('사진 업로드를 기다리고 있어요', '업로드 중인 창을 유지해 주세요. 전송이 멈췄다면 보관한 사진에서 이어서 올려 주세요.', 'input', '사진 업로드 확인', true, 2500);
        if (prep.state === 'retry_pending') return show('멈춘 단계부터 다시 준비하고 있어요', kept, 'prepare-retry', '이어가기 확인', true, 2500);
        const titles: Record<string, string> = { reading: '사진을 읽고 있어요', validating: '읽은 내용을 확인하고 있어요', saving: '읽은 내용을 저장하고 있어요', generating: '풀이를 만들고 있어요' };
        return show(titles[prep.state] || '기존 요청을 확인하고 있어요', kept, '', '', true, 2500);
    }
    if (run.execution_kind === 'limited_test') return show('접수만 보관된 요청이에요', '아직 작성된 풀이가 없어요. 이 요청은 생성이 진행되지 않아요.');
    if (run.paused) return show('풀이를 잠시 멈췄어요', kept, 'resume', '이어서 만들기');
    if (run.connection_trial && tasks.some((t: any) => t.status === 'completed') &&
        tasks.some((t: any) => t.status === 'queued') && !tasks.some((t: any) => t.status === 'running'))
        return show('저장된 풀이 다음부터 이어갈 수 있어요', '다시 시도하면 아직 시작하지 않은 부분만 만들어요. 완성된 내용은 아래에서 계속 읽을 수 있어요.', 'resume', '다시 시도');
    if (tasks.some((t: any) => ['running', 'queued'].includes(t.status) && !t.blocked_on?.length)) return show('풀이를 만들고 있어요', '완성된 내용부터 아래에서 읽을 수 있어요.', '', '', true, 2500);
    if (tasks.some((t: any) => t.status === 'waiting_input')) return show('추가 정보가 필요해요', '아래의 필요한 정보를 확인해 주세요. 저장된 본문은 계속 읽을 수 있어요.', 'input', '필요한 정보 확인');
    if (run.generation_completion === 'incomplete' || run.connection_trial || tasks.some((t: any) => t.status !== 'completed'))
        return show('일부 풀이가 준비됐어요', '아래의 저장된 내용을 읽을 수 있어요. 전체 풀이가 완성된 상태는 아니에요.');
    return show('풀이가 완성됐어요', run.generation_completion === 'completed_with_limits' ? '확인된 자료 범위의 풀이를 마쳤어요. 자료가 부족한 부분은 본문에 표시했어요.' : '아래에서 저장된 풀이를 읽을 수 있어요.');
}
export function connectionFailure(code: string): { title: string; detail: string } | null {
    const messages: Record<string, [string, string]> = {
        SUBSCRIPTION_LIMIT_REACHED: ['사용 한도에 도달해 새 풀이를 멈췄어요', 'ChatGPT 설정의 사용량을 확인해 주세요. 다시 로그인할 필요는 없어요. 저장된 풀이는 계속 읽을 수 있어요.'],
        QUOTA_EXCEEDED: ['사용 한도 확인이 필요해요', '새 풀이를 멈췄어요. 사용 한도를 확인해 주세요. 저장된 풀이는 계속 읽을 수 있어요.'],
        PERMISSION_DENIED: ['풀이를 사용할 권한을 확인해야 해요', '연결 권한을 서비스에서 확인해야 해요. 사진을 다시 올리거나 로그인을 반복할 필요는 없어요.'],
        SUBSCRIPTION_INELIGIBLE: ['현재 계정에서는 구독 풀이를 사용할 수 없어요', '선택한 ChatGPT 계정이나 작업공간의 사용 자격을 확인해 주세요. 저장한 풀이는 유지됩니다.'],
        SUBSCRIPTION_RECONNECT_REQUIRED: ['ChatGPT를 다시 연결해야 해요', '기존 연결이 만료되었어요. 다시 연결한 뒤 멈춘 풀이를 이어갈 수 있어요. 연결 기능은 아직 준비 중이에요.'],
        SUBSCRIPTION_REFRESH_UNCERTAIN: ['ChatGPT 연결 갱신을 확인하고 있어요', '갱신 완료 여부를 확인할 때까지 새 풀이를 멈췄어요. 기존 연결 정보와 풀이는 보관되어 있어요.'],
        UNSUPPORTED_MODEL: ['현재 모델로는 풀이를 진행할 수 없어요', '서비스에서 지원 모델을 확인해야 해요. 저장된 내용은 유지됩니다.'],
        UNSUPPORTED_REQUEST: ['풀이 요청 방식을 확인해야 해요', '서비스에서 요청 방식을 수정해야 해요. 같은 요청을 반복하거나 사진을 다시 올릴 필요는 없어요.'],
        UNSUPPORTED_INPUT: ['풀이 요청을 확인해야 해요', '서비스에서 지원하는 요청 형식을 확인해야 해요. 저장된 내용은 유지됩니다.'],
        SERVICE_REQUEST_REJECTED: ['사진 분석 연결을 확인해야 해요', '서비스에서 요청 방식을 확인해야 해요. 사진을 다시 올릴 필요는 없어요.']
    };
    return messages[code] ? { title: messages[code][0], detail: messages[code][1] } : null;
}
export function taskMessage(task: any): string {
    if (task.delivery_state === 'completed_pending_save') return '풀이 응답을 받았어요. 저장이 끝나면 읽을 수 있어요. 새 풀이를 요청하지 않아요.';
    const message = connectionFailure(task.error_code);
    if (message) return message.detail;
    if (task.failure_kind === 'unknown' || task.recovery_required) return '기존 요청의 처리 여부를 확인해야 해요. 확인 전에는 다시 보내지 않아요.';
    if (task.failure_kind === 'configuration') return task.can_retry ? '서비스 설정이 바뀌어 다시 시도할 수 있어요.' : '서비스 설정·권한 확인이 필요해요. 사진을 다시 올릴 필요는 없어요.';
    if (task.error_code === 'GENERATION_LIMIT_REACHED') return '연결 시험 호출 한도에 도달했습니다. 저장된 본문은 다시 읽을 수 있습니다.';
    if (task.error_code === 'GENERATION_WORKER_UNAVAILABLE') return '서버 작업을 시작하지 못했습니다. 저장된 사주와 기존 결과는 유지됩니다.';
    if (task.connection_deferred) return '이 항목은 이번 연결 시험 범위에 포함되지 않습니다.';
    if (task.acceptance_only) return '실행 입력 접수만 확인했습니다. 외부 모델 생성은 시작하지 않았습니다.';
    if (task.implementation_pending) return '해당 관점 또는 종합은 생성 연결 준비 중입니다. 저장된 A 해석은 계속 읽을 수 있습니다.';
    if (task.generation_available === false) return '장별 항목 계약은 등록되었으며, 본문 생성 연결은 준비 중입니다.';
    if (task.status === 'cancelled') return '최종 취소된 항목입니다. 다시 생성하려면 새 실행을 준비해 주세요.';
    if (task.status === 'completed') return '저장된 풀이를 읽을 수 있어요.';
    if (task.status === 'waiting_input') return '정보 또는 실행 조건을 확인한 뒤 새 실행을 준비해 주세요.';
    if (task.recovery_required) return '이전 요청의 처리 여부가 불확실합니다. 재시도하면 중복 처리·과금이 발생할 수 있습니다.';
    if (task.recovery_action === 'attempt_limit_review') return '재시도 한도에 도달했습니다. 원인을 확인한 뒤 새 실행을 준비해 주세요.';
    if (task.recovery_action === 'fix_request_new_run') return '실행 계약 또는 서버 설정 확인이 필요합니다. 자료 누락으로 인한 입력 대기는 아닙니다.';
    if (task.status === 'failed') {
        const messages: Record<string, string> = {
            OUTPUT_TRUNCATED: '공급자의 출력 한도에서 응답이 잘렸습니다. 완성 본문으로 저장하지 않았습니다.',
            OUTPUT_INCOMPLETE: '공급자가 응답 완료를 확인하지 않았습니다. 처리 상태를 확인해야 합니다.',
            PROVIDER_REJECTED: '공급자가 이 생성 요청을 거절했습니다. 완료 본문은 없습니다.',
            AUTHENTICATION_ERROR: '풀이 서비스의 공급자 인증·접근 권한 확인이 필요합니다. 사주를 다시 입력할 필요는 없습니다.',
            QUOTA_EXCEEDED: '공급자 계정의 결제·사용 한도 확인이 필요합니다.',
            MODEL_UNAVAILABLE: '풀이 모델의 서버 설정 또는 사용 권한 확인이 필요합니다.',
            GENERATION_LIMIT_REACHED: '연결 시험 호출 한도에 도달했습니다. 저장된 본문은 다시 읽을 수 있습니다.',
            GENERATION_WORKER_UNAVAILABLE: '서버 작업을 시작하지 못했습니다. 저장된 사주와 기존 결과는 유지됩니다.'
        };
        if (messages[task.error_code]) return messages[task.error_code];
        if (['RATE_LIMITED','PROVIDER_FAILURE','TIMEOUT','TRANSPORT_ERROR'].includes(task.error_code)) return '일시적인 생성 서비스 문제입니다. 실패한 항목만 다시 시도할 수 있습니다.';
        if (['RESPONSE_SCHEMA_ERROR','INCOMPLETE_OUTPUT','REFUSAL'].includes(task.error_code)) return '응답을 완성된 결과로 확인하지 못했습니다. 입력을 확인하거나 실패한 항목을 다시 시도해 주세요.';
        return '생성 설정 또는 응답을 확인하지 못했습니다. 입력·서비스 설정 확인 후 다시 시도해 주세요.';
    }
    if (task.blocked_on?.length) return '앞선 항목의 실패 또는 입력 확인을 기다리고 있습니다.';
    if (task.recovery_action === 'scheduled_retry') return '일시 실패 후 서버가 재시도를 기다리고 있습니다.';
    return task.status === 'running' ? '서버에서 처리 중입니다. 페이지를 닫아도 실행은 저장됩니다.' : '서버 실행 순서를 기다리고 있습니다.';
}
// Render only contract content, always via escaped interpolation (never generated HTML).
export function readingBlocks(result: any, output: any): any[] {
    if (!result) return [];
    if (output?.adapter === 'f1_chapters' && typeof result.summary === 'string' && Array.isArray(result.paragraphs)) {
        const topics: any = {intimacy:'친밀해지는 방식', expectation_expression:'기대와 표현', romantic_conflict:'갈등 반응',
            work_environment:'업무 환경', collaboration_decisions:'협업과 결정', work_exhaustion:'지치기 쉬운 상황',
            income_habits:'수입 습관', spending_habits:'소비 습관', money_management:'관리 습관',
            load_accumulation:'부담 누적', strength_overuse:'장점의 과잉', adjustment_actions:'조절 행동'};
        const scopes = (result.life_basis?.topic_scopes || []).map((scope: any) =>
            (topics[scope.topic] || '생활 해석') + (scope.status === 'limited' ? ' · 판단 제한' : ' · 해석 범위') +
            '\n' + scope.reason + '\n' + scope.possible_range);
        const sources: string[] = [];
        if (result.source_scope) {
            try {
                const scope = JSON.parse(result.source_scope);
                if (Array.isArray(scope)) scope.forEach((row: any) => sources.push(
                    row.name + ' · ' + (row.state === 'confirmed_absent' ? '해당 표기 범위에서 없음 확인' : '확인된 항목') +
                    ' · ' + (row.origin === 'program_calculation' ? '검증 근거가 등록된 계산값' : '외부 자료·직접 입력 확인값')));
                else {
                    const labels: any = {direction:'배열 방향', start_rule:'시작 기준', age_convention:'나이 표기', year_boundary:'연도 경계', period_end:'끝 포함 기준', date_calendar:'날짜 역법', start_age:'최초 시작 나이', start_date:'최초 시작 날짜'};
                    const values: any = {forward:'순행', reverse:'역행', year:'연나이', counting:'세는나이', international:'만나이', full:'만나이', january1:'1월 1일', solar_term:'절기', lunar_new_year:'음력 새해', source_defined:'출처 지정', inclusive:'끝 포함', exclusive:'끝 제외', solar:'양력', unknown:'미상'};
                    for (const [key, field] of Object.entries({...scope.basis, ...scope.start_fields}) as any)
                        sources.push((labels[key] || key) + ': ' + (values[field.normalized] || field.normalized));
                    for (const row of scope.periods || []) {
                        const v = row.values;
                        sources.push((v.order === undefined ? '' : v.order + '번째 · ') + (v.ganzhi || '') + ' · ' +
                            (v.start_date ? v.start_date + ' ~ ' + v.end_date : v.start_year !== undefined ? v.start_year + ' ~ ' + v.end_year + '년' : '연도 미확인') +
                            (v.start_age !== undefined ? ' · ' + v.start_age + ' ~ ' + v.end_age + '세 (제공 기준)' : ' · 나이 미확인'));
                    }
                    sources.push(scope.current_note);
                }
            } catch { sources.push('저장된 출처 정보를 확인할 수 없습니다.'); }
        }
        return [{ title:result.title, sourceNotes:sources, scopeNotes:scopes,
            body:[result.summary, ...result.paragraphs,
                ...(sources.length ? ['확인 자료와 적용 범위\n' + sources.join('\n')] : []),
                ...(scopes.length ? ['주제별 판단 범위\n' + scopes.join('\n\n')] : []),
                ...(result.cautions.length ? ['주의\n' + result.cautions.join('\n')] : []),
                ...(result.limitations.length ? ['자료 제한\n' + result.limitations.join('\n')] : [])].join('\n\n'),
            evidence:result.judgments.map((j: any) => [j.text, '조건: ' + j.conditions, '해석의 한계: ' + j.uncertainty].join('\n')).join('\n\n'),
            advice:result.advice.join('\n\n') }];
    }
    if (output?.adapter === 'f1_chapters' && result.chapters)
        return Object.entries(result.chapters).flatMap(([id, value]) => readingBlocks(value, output).map(block => ({...block, sourcePath:'chapters/' + id + '/body'})));
    if (result.paragraphs) return [{ title:result.item_id,
        body:result.paragraphs.map((p: any) => p.text).join('\n\n') + (result.coverage === 'limited' ? '\n\n자료 제한: ' + result.limitation.reason + '\n필요 정보: ' + result.limitation.missing + '\n영향 범위: ' + result.limitation.impact : ''),
        evidence:result.summary.evidence, advice:'' }];
    if (result.contract_version === '1.0.0' && result.sections && result.perspective)
        return Object.entries(result.sections).map(([id, section]: any) => ({
            title: output.sections.find((s: any) => s.id === id)?.title || id,
            body: section.sentences.join(' '), evidence: section.evidence_line, advice: '',
            conditions: section.conditions.join(' · '), limitations: section.limitations.join(' · ') }));
    if (result.perspectives && result.synthesis) return [
        ...Object.entries(result.perspectives).flatMap(([id, value]) => readingBlocks(value, output).map(block => ({...block,
            label: (output.perspectives.find((p: any) => p.id === id)?.title || id) + ' · ' + (block.label || block.title || ''),
            sourcePath: 'perspectives/' + id + '/' + block.sourcePath}))),
        ...readingBlocks(result.synthesis, output).map(block => ({...block, sourcePath: 'synthesis/' + block.sourcePath}))
    ];
    if (result.chapters) return Object.entries(result.chapters).map(([id, value]: any) => ({ sourcePath:'chapters/' + id + '/body', label: output.sections.find((s: any) => s.id === id)?.title || id, ...value }));
    if (typeof result.body === 'string') return [{...result, sourcePath:'body'}];
    if (result.completion === 'content_complete' && Array.isArray(result.common)) return [];
    if (result.common !== undefined) return [{title:'공통점', body:result.common, sourcePath:'common'}, {title:'해석 차이', body:result.differences, sourcePath:'differences'}, ...(result.actions || []).map((body: string, i: number) => ({title:'실천 행동 ' + (i + 1), body, sourcePath:'actions/' + i}))];
    return Object.entries(result).filter(([_,v]: any) => v && typeof v.body === 'string').map(([id,v]: any) => ({sourcePath:id + '/body',label:output.sections.find((s: any) => s.id === id)?.title || id,...v}));
}
